import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  LockKeyhole,
  ShieldCheck,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { getCartAccessToken, useCart } from "../lib/cart";
import { SmallCaps } from "../components/atelier/primitives";
import Header from "../components/layout/Header";
import { useI18n } from "../lib/i18n";
import { apiErrorMessage } from "../lib/apiErrors";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import { AZ_PREFIX, e164Phone, formatLocalPhone, localPhoneDigits, maskedPhone } from "../lib/phone";
import { analytics } from "../lib/analytics";

const FOCUS = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-espresso";
const CART_ID_KEY = "atelier_cart_id";

function onlyOtpDigits(value) {
  return String(value || "").replace(/\D/g, "").slice(0, 6);
}

function safeNext(search) {
  const value = new URLSearchParams(search).get("next") || "/account";
  return value.startsWith("/") && !value.startsWith("//") ? value : "/account";
}

function formatTimer(seconds) {
  const safe = Math.max(0, Number(seconds) || 0);
  return `00:${String(safe).padStart(2, "0")}`;
}

function isTermsRequired(error) {
  return String(error?.response?.data?.detail || "").includes("Terms acceptance is required");
}

export default function Auth({ mode = "login" }) {
  const { t } = useI18n();
  useDocumentTitle(mode === "register" ? "Qeydiyyat" : "Daxil ol");
  const { requestOtp, verifyOtp, login } = useAuth();
  const cartContext = useCart() || {};
  const { cart, adoptCart } = cartContext;
  const navigate = useNavigate();
  const location = useLocation();
  const next = useMemo(() => safeNext(location.search), [location.search]);
  const otpPurpose = next.startsWith("/checkout") ? "checkout" : "login";
  const otpInputRef = useRef(null);
  const firstNameRef = useRef(null);
  const attemptedOtpRef = useRef("");
  const redirectTimerRef = useRef(null);

  const [stage, setStage] = useState("phone");
  const [phoneDigits, setPhoneDigits] = useState("");
  const [verifiedPhone, setVerifiedPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [profile, setProfile] = useState({
    first_name: "", last_name: "", email: "", marketing_consent: false,
    new_password: "", confirm_password: "",
  });
  const [debugCode, setDebugCode] = useState("");
  const [resendSeconds, setResendSeconds] = useState(0);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState("");

  const phone = e164Phone(phoneDigits);
  const phoneValid = Boolean(phone);
  const profileReady = profile.first_name.trim().length > 1 && profile.last_name.trim().length > 1;
  // The password is chosen here or never. Sign-up used to send none, so the
  // backend fell back to `secrets.token_urlsafe(32)` and every storefront
  // account carried a password nobody could ever type — while this same page
  // offered a "Parolla daxil ol" button that could not possibly work for them.
  const passwordProblem = () => {
    if (profile.new_password.length < 8) return "Parol ən azı 8 simvol olmalıdır.";
    if (profile.new_password !== profile.confirm_password) return "Parollar uyğun gəlmir.";
    return "";
  };

  useEffect(() => () => window.clearTimeout(redirectTimerRef.current), []);

  useEffect(() => {
    if (resendSeconds <= 0) return undefined;
    const timer = setInterval(() => {
      setResendSeconds((current) => Math.max(0, current - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendSeconds]);

  useEffect(() => {
    if (stage === "otp") otpInputRef.current?.focus();
    if (stage === "profile") firstNameRef.current?.focus();
  }, [stage]);

  useEffect(() => {
    if (stage !== "otp" || otp.length !== 6 || busy || attemptedOtpRef.current === otp) return;
    verifyCode(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp, stage, busy]);

  const cartPayload = () => {
    const cartId = cart?.id || window.localStorage.getItem(CART_ID_KEY);
    return {
      cart_id: cartId || undefined,
      cart_access_token: getCartAccessToken() || undefined,
    };
  };

  const goAfterSuccess = async (result) => {
    if (result?.cart_id && adoptCart) {
      try { await adoptCart(result.cart_id); }
      catch (error) { console.warn("[atelier] claimed cart could not be adopted", error); }
    }
    setStage("success");
    redirectTimerRef.current = window.setTimeout(() => navigate(next, { replace: true }), 700);
  };

  const sendCode = async () => {
    if (!phoneValid || busy) return;
    setBusy(true);
    setErr("");
    setDebugCode("");
    attemptedOtpRef.current = "";
    setOtp("");
    setVerifiedPhone(phone);
    // Open the code field immediately and let the user wait there — the OTP send
    // is a synchronous gateway round-trip, so blocking the phone screen on it read
    // as a hang. Fire the request in the background; on failure fall back. A
    // provisional resend window stops a double-send until the response lands.
    setResendSeconds(60);
    setStage("otp");
    try {
      const response = await requestOtp(phone, otpPurpose);
      setVerifiedPhone(response.phone || phone);
      setResendSeconds(response.resend_after_seconds || 60);
      setDebugCode(response.debug_code || "");
    } catch (error) {
      setErr(apiErrorMessage(error, t));
      setStage("phone");
    } finally {
      setBusy(false);
    }
  };

  const resendCode = async () => {
    if (busy || resendSeconds > 0) return;
    await sendCode();
  };

  const verifyCode = async (withProfile) => {
    if (otp.length !== 6 || busy) return;
    if (withProfile && !profileReady) {
      setErr("Davam etmək üçün ad və soyadınızı yazın.");
      return;
    }
    if (withProfile) {
      const problem = passwordProblem();
      if (problem) { setErr(problem); return; }
    }
    setBusy(true);
    setErr("");
    attemptedOtpRef.current = otp;
    try {
      const result = await verifyOtp({
        phone: verifiedPhone || phone,
        code: otp,
        purpose: otpPurpose,
        terms_accepted: Boolean(withProfile),
        first_name: withProfile ? profile.first_name.trim() : null,
        last_name: withProfile ? profile.last_name.trim() : null,
        email: withProfile && profile.email.trim() ? profile.email.trim() : null,
        marketing_consent: Boolean(profile.marketing_consent),
        password: withProfile ? profile.new_password : undefined,
        ...cartPayload(),
      });
      // The profile step only appears for a number with no account yet.
      if (withProfile) analytics.signUp("phone");
      await goAfterSuccess(result);
    } catch (error) {
      if (!withProfile && isTermsRequired(error)) {
        setStage("profile");
        setErr("");
      } else {
        setErr(apiErrorMessage(error, t));
      }
    } finally {
      setBusy(false);
    }
  };

  // Hybrid auth: a password is chosen at sign-up, so it has to be usable to get
  // back in. The page offered only SMS and never said the word "parol", which
  // left the password half of the design unreachable.
  const signInWithPassword = async () => {
    if (!phoneValid || password.length < 1 || busy) return;
    setBusy(true);
    setErr("");
    try {
      await login(phone, password);
      await goAfterSuccess({});
    } catch (error) {
      setErr(apiErrorMessage(error, t));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (stage === "phone") await sendCode();
    if (stage === "password") await signInWithPassword();
    if (stage === "otp") await verifyCode(false);
    if (stage === "profile") await verifyCode(true);
  };

  const changePhone = () => {
    setStage("phone");
    setErr("");
    setOtp("");
    setDebugCode("");
    setResendSeconds(0);
    attemptedOtpRef.current = "";
  };

  return (
    <div data-testid={`auth-${mode}`} className="min-h-screen bg-ivory text-graphite">
      <Header quiet />
      <main id="main" className="adf-auth-shell">
        {/* One column, one job: sign in. The page used to run a marketing
            column beside the form — an italic headline, a paragraph and three
            benefit rows — while every step of the form already carried its own
            eyebrow, title and subcopy. The customer read the same message
            twice and the actual input sat in the right-hand third. */}
        <section className="adf-auth-container">
          <Link to="/shop" className={`adf-auth-back ${FOCUS}`}>
            <ArrowLeft size={16} strokeWidth={1.5} aria-hidden="true" />
            Alış-verişə qayıt
          </Link>

          <form className="adf-auth-panel" onSubmit={submit} data-testid="auth-form" noValidate={stage !== "profile"}>
            {stage === "phone" && (
              <PhoneStep
                phoneDigits={phoneDigits}
                onPhoneChange={(value) => {
                  setPhoneDigits(localPhoneDigits(value));
                  setErr("");
                  setOtp("");
                  attemptedOtpRef.current = "";
                }}
                phoneValid={phoneValid}
                busy={busy}
                err={err}
                focusClass={FOCUS}
              />
            )}

            {stage === "password" && (
              <PasswordStep
                phoneDigits={phoneDigits}
                onPhoneChange={(value) => { setPhoneDigits(localPhoneDigits(value)); setErr(""); }}
                phoneValid={phoneValid}
                password={password}
                setPassword={(value) => { setPassword(value); setErr(""); }}
                busy={busy}
                err={err}
                focusClass={FOCUS}
                onUseCode={() => { setStage("phone"); setErr(""); setPassword(""); }}
              />
            )}

            {stage === "otp" && (
              <OtpStep
                otp={otp}
                setOtp={(value) => {
                  setOtp(onlyOtpDigits(value));
                  setErr("");
                }}
                inputRef={otpInputRef}
                phone={verifiedPhone || phone}
                busy={busy}
                err={err}
                debugCode={debugCode}
                resendSeconds={resendSeconds}
                onResend={resendCode}
                onChangePhone={changePhone}
                focusClass={FOCUS}
              />
            )}

            {stage === "profile" && (
              <ProfileStep
                profile={profile}
                setProfile={setProfile}
                firstNameRef={firstNameRef}
                phone={verifiedPhone || phone}
                busy={busy}
                err={err}
                focusClass={FOCUS}
              />
            )}

            {stage === "success" && <SuccessStep />}

            {stage !== "success" && (
              <div className="adf-auth-actions">
                <button
                  type="submit"
                  data-testid="auth-submit"
                  disabled={
                    busy
                    || (stage === "phone" && !phoneValid)
                  || (stage === "password" && (!phoneValid || password.length < 1))
                    || (stage === "otp" && otp.length !== 6)
                    || (stage === "profile" && !profileReady)
                  }
                  className={`adf-auth-primary ${FOCUS}`}
                >
                  {stage === "phone" && (busy ? "Kod göndərilir..." : "Kodu göndər")}
                  {stage === "password" && (busy ? "Yoxlanılır..." : "Daxil ol")}
                  {stage === "otp" && (busy ? "Kod yoxlanılır..." : "Kodu təsdiqlə")}
                  {stage === "profile" && (busy ? "Hesab yaradılır..." : "Davam et")}
                </button>
                {stage === "phone" && (
                  <button
                    type="button"
                    onClick={() => { setStage("password"); setErr(""); }}
                    data-testid="auth-use-password"
                    className={`adf-auth-text-action justify-self-start ${FOCUS}`}
                  >
                    Parolla daxil ol
                  </button>
                )}
                {stage === "phone" && (
                  <p className="adf-auth-legal">
                    Davam etməklə <Link to="/privacy">Məxfilik siyasəti</Link> və{" "}
                    <Link to="/terms">İstifadə şərtləri</Link> ilə razılaşmış olursunuz.
                  </p>
                )}
                <div className="adf-auth-trust">
                  <ShieldCheck size={16} strokeWidth={1.6} aria-hidden="true" />
                  <span>Məlumatlarınız təhlükəsiz şəkildə qorunur.</span>
                </div>
              </div>
            )}
          </form>
        </section>
      </main>
      <AuthFooter />
    </div>
  );
}

function PhoneStep({ phoneDigits, onPhoneChange, phoneValid, busy, err, focusClass }) {
  return (
    <>
      <div>
        <SmallCaps>Şəxsi hesab</SmallCaps>
        <h2 className="adf-auth-panel-title">Telefon nömrənizlə davam edin</h2>
        <p className="adf-auth-panel-copy">Birdəfəlik təsdiq kodunu SMS vasitəsilə göndərəcəyik.</p>
      </div>

      <label className="adf-auth-field">
        <span>Telefon nömrəsi</span>
        <div className={`adf-auth-phone-control ${phoneValid ? "is-valid" : ""}`}>
          <div className="adf-auth-country" aria-hidden="true">
            <span>🇦🇿</span>
            <strong>{AZ_PREFIX}</strong>
          </div>
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            name="tel"
            data-testid="auth-phone"
            value={formatLocalPhone(phoneDigits)}
            onChange={(event) => onPhoneChange(event.target.value)}
            onPaste={(event) => {
              event.preventDefault();
              onPhoneChange(event.clipboardData.getData("text"));
            }}
            placeholder="(50) 123 45 67"
            aria-describedby={err ? "auth-error" : "auth-phone-help"}
            className={focusClass}
            disabled={busy}
          />
        </div>
      </label>
      <p id="auth-phone-help" className="adf-auth-hint">Azərbaycan mobil nömrəsini daxil edin.</p>
      {err && <AuthError>{err}</AuthError>}
    </>
  );
}

function PasswordStep({ phoneDigits, onPhoneChange, phoneValid, password, setPassword, busy, err, focusClass, onUseCode }) {
  return (
    <>
      <div>
        <SmallCaps>Şəxsi hesab</SmallCaps>
        <h2 className="adf-auth-panel-title">Parolla daxil olun</h2>
        <p className="adf-auth-panel-copy">Qeydiyyat zamanı təyin etdiyiniz parolu istifadə edin.</p>
      </div>

      <label className="adf-auth-field">
        <span>Telefon nömrəsi</span>
        <div className={`adf-auth-phone-control ${phoneValid ? "is-valid" : ""}`}>
          <div className="adf-auth-country" aria-hidden="true">
            <span>🇦🇿</span>
            <strong>{AZ_PREFIX}</strong>
          </div>
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            data-testid="auth-password-phone"
            value={formatLocalPhone(phoneDigits)}
            onChange={(event) => onPhoneChange(event.target.value)}
            placeholder="(50) 123 45 67"
            className={focusClass}
            disabled={busy}
          />
        </div>
      </label>

      <label className="adf-auth-field">
        <span>Parol</span>
        <input
          type="password"
          placeholder="••••••••"
          autoComplete="current-password"
          data-testid="auth-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          className={`adf-auth-line-input ${focusClass}`}
          disabled={busy}
          aria-describedby={err ? "auth-error" : undefined}
        />
      </label>

      <div className="adf-auth-inline-links">
        <Link to="/reset-password" className={`adf-auth-text-action ${focusClass}`} data-testid="auth-forgot">
          Parolu unutmusunuz?
        </Link>
        <button type="button" onClick={onUseCode} className={`adf-auth-text-action ${focusClass}`} data-testid="auth-use-code">
          SMS kodu ilə davam et
        </button>
      </div>
      {err && <AuthError>{err}</AuthError>}
    </>
  );
}

function OtpStep({
  otp,
  setOtp,
  inputRef,
  phone,
  busy,
  err,
  debugCode,
  resendSeconds,
  onResend,
  onChangePhone,
  focusClass,
}) {
  return (
    <>
      <div>
        <SmallCaps>Təsdiq</SmallCaps>
        <h2 className="adf-auth-panel-title">Kodu daxil edin</h2>
        <p className="adf-auth-panel-copy">
          {maskedPhone(phone)} nömrəsinə göndərilən 6 rəqəmli kodu daxil edin.
        </p>
      </div>

      <label className="adf-auth-otp-label" htmlFor="auth-code">SMS kodu</label>
      <div className="adf-auth-otp-wrap">
        <input
          ref={inputRef}
          id="auth-code"
          type="text"
          placeholder="000000"
          inputMode="numeric"
          autoComplete="one-time-code"
          name="one-time-code"
          data-testid="auth-code"
          value={otp}
          onChange={(event) => setOtp(event.target.value)}
          onPaste={(event) => {
            event.preventDefault();
            setOtp(event.clipboardData.getData("text"));
          }}
          aria-describedby={err ? "auth-error" : "auth-otp-help"}
          className={`adf-auth-otp-native ${focusClass}`}
          disabled={busy}
        />
        <div className="adf-auth-otp-cells" aria-hidden="true">
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <span key={index} className={otp.length === index ? "is-active" : otp[index] ? "is-filled" : ""}>
              {otp[index] || ""}
            </span>
          ))}
        </div>
      </div>
      <p id="auth-otp-help" className="adf-auth-hint">
        Altı rəqəm tamamlananda kod avtomatik yoxlanılır.
      </p>
      {debugCode && <div className="adf-auth-debug" data-testid="auth-debug-code">Dev kod: {debugCode}</div>}
      {err && <AuthError>{err}</AuthError>}

      <div className="adf-auth-secondary-actions">
        <button type="button" onClick={onChangePhone} className={`adf-auth-text-action ${focusClass}`}>
          Nömrəni dəyiş
        </button>
        <button
          type="button"
          onClick={onResend}
          disabled={busy || resendSeconds > 0}
          className={`adf-auth-text-action ${focusClass}`}
          data-testid="auth-resend"
        >
          {resendSeconds > 0 ? `Kodu yenidən göndər — ${formatTimer(resendSeconds)}` : "Kodu yenidən göndər"}
        </button>
      </div>
    </>
  );
}

function ProfileStep({ profile, setProfile, firstNameRef, phone, busy, err, focusClass }) {
  const update = (field, value) => setProfile((current) => ({ ...current, [field]: value }));
  return (
    <>
      <div>
        <SmallCaps>Profil</SmallCaps>
        <h2 className="adf-auth-panel-title">Hesabınızı tamamlayın</h2>
        <p className="adf-auth-panel-copy">
          {maskedPhone(phone)} təsdiqləndi. Bundan sonra ya SMS kodu ilə, ya da burada
          seçdiyiniz parolla daxil ola bilərsiniz.
        </p>
      </div>

      <div className="adf-auth-profile-grid">
        <ProfileField
          ref={firstNameRef}
          label="Ad"
          value={profile.first_name}
          onChange={(value) => update("first_name", value)}
          autoComplete="given-name"
          testid="auth-first-name"
          focusClass={focusClass}
          disabled={busy}
          required
        />
        <ProfileField
          label="Soyad"
          value={profile.last_name}
          onChange={(value) => update("last_name", value)}
          autoComplete="family-name"
          testid="auth-last-name"
          focusClass={focusClass}
          disabled={busy}
          required
        />
        <ProfileField
          label="E-poçt (istəyə bağlı)"
          type="email"
          value={profile.email}
          onChange={(value) => update("email", value)}
          autoComplete="email"
          testid="auth-email"
          focusClass={focusClass}
          disabled={busy}
        />
        {/* Chosen once, here. There is no other moment in the storefront where
            a customer can set one, so leaving it out is what made the password
            half of the hybrid design unreachable. */}
        <ProfileField
          label="Parol"
          type="password"
          value={profile.new_password}
          onChange={(value) => update("new_password", value)}
          autoComplete="new-password"
          testid="auth-new-password"
          focusClass={focusClass}
          disabled={busy}
          required
        />
        <ProfileField
          label="Parolu təkrarlayın"
          type="password"
          value={profile.confirm_password}
          onChange={(value) => update("confirm_password", value)}
          autoComplete="new-password"
          testid="auth-confirm-password"
          focusClass={focusClass}
          disabled={busy}
          required
        />
      </div>
      <p className="adf-auth-hint">Ən azı 8 simvol.</p>

      <label className="adf-auth-consent">
        <input
          type="checkbox"
          checked={profile.marketing_consent}
          onChange={(event) => update("marketing_consent", event.target.checked)}
          data-testid="auth-marketing"
        />
        <span>Atelier xəbərləri və şəxsi təklifləri SMS/e-poçtla almağa razıyam.</span>
      </label>
      <p className="adf-auth-legal is-profile">
        Davam etməklə <Link to="/privacy">Məxfilik siyasəti</Link> və{" "}
        <Link to="/terms">İstifadə şərtləri</Link> ilə razılaşırsınız.
      </p>
      {err && <AuthError>{err}</AuthError>}
    </>
  );
}

const ProfileField = React.forwardRef(function ProfileField(
  { label, value, onChange, type = "text", autoComplete, testid, focusClass, disabled, required, placeholder },
  ref,
) {
  return (
    <label className="adf-auth-field">
      <span>{label}</span>
      <input
        ref={ref}
        type={type}
        value={value}
        required={required}
        disabled={disabled}
        placeholder={placeholder || (type === "password" ? "••••••••" : label)}
        onChange={(event) => onChange(event.target.value)}
        autoComplete={autoComplete}
        data-testid={testid}
        className={`adf-auth-line-input ${focusClass}`}
      />
    </label>
  );
});

function SuccessStep() {
  return (
    <div className="adf-auth-success" data-testid="auth-success" role="status" aria-live="polite">
      <CheckCircle2 size={42} strokeWidth={1.35} aria-hidden="true" />
      <SmallCaps>Hazırdır</SmallCaps>
      <h2>Uğurla təsdiqləndi</h2>
    </div>
  );
}

function AuthError({ children }) {
  return (
    <div className="adf-auth-error" id="auth-error" data-testid="auth-error" role="alert">
      <LockKeyhole size={16} strokeWidth={1.55} aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

function AuthFooter() {
  return (
    <footer className="adf-auth-footer" data-testid="auth-footer">
      <div>© 2026 Atelier Di Falco</div>
      <nav aria-label="Auth footer">
        <Link to="/privacy">Məxfilik siyasəti</Link>
        <Link to="/terms">İstifadə şərtləri</Link>
        <Link to="/contact">Əlaqə</Link>
      </nav>
    </footer>
  );
}
